import { functions } from 'helpers';
import { constants } from 'helpers/constants';
import { formatDate } from 'helpers/date';
import type ICoProducer from 'interfaces/ICoProducer';
import { EChargeType } from 'interfaces/IEditProduct';
import type IProduct from 'interfaces/IProduct';

class Product implements IProduct {
  public active: number = this.product.active === null || this.product.active === undefined ? 1 : this.product.active;
  public affiliate_instructions: string = this.product.affiliate_instructions || '';
  public affiliation_contract: string = this.product.affiliation_contract || '';
  public affiliation_status: string = this.product.affiliation_status || '';
  public allow_affiliation: boolean = this.product.allow_affiliation || false;
  public allows_boleto: boolean = this.product.allows_boleto || false;
  public allows_card: boolean = this.product.allows_card || false;
  public allows_downgrade: boolean = this.product.allows_downgrade || false;
  public allows_eduzz_balance: boolean = this.product.allows_eduzz_balance || false;
  public allows_multiple_cards: boolean = this.product.allows_multiple_cards || false;
  public allows_paypal: boolean = this.product.allows_paypal || false;
  public allows_pix: boolean = this.product.allows_pix || false;
  public allows_automatic_pix: boolean = this.product.allows_automatic_pix || false;
  public allows_combine_payment: boolean = this.product.allows_combine_payment || false;
  public credit_card_installments: { producer: boolean; product: boolean } = this.product.credit_card_installments || {
    producer: false,
    product: false
  };
  public allows_upgrade: boolean = this.product.allows_upgrade || false;
  public alternative_url: string = this.product.alternative_url || '';
  public ask_address_checkout: boolean = this.product.ask_address_checkout || false;
  public ask_address_thanks: boolean = this.product.ask_address_thanks || false;
  public author_name: string = this.product.author_name || '';
  public auto_affiliation: boolean = this.product.auto_affiliation || false;
  public auto_refund: boolean = this.product.auto_refund || false;
  public beta: string[] = this.product?.beta || [];
  public can_create_pixel: boolean = this.product.can_create_pixel || false;
  public categories: any[] = this.product.categories || [];
  public charge_type: string = this.product.charge_type || 'N';
  public cnt_partner_cod: number = this.product.cnt_partner_cod || null;
  public comission_type: string = this.product.comission_type || 'P';
  public comission_value: number = this.product.comission_value || 0;
  public connects: number = this.product.connects || 0;
  public coproducers: Array<ICoProducer> = this.product.coproducers || [];
  public currency_id: number = this.product.currency_id || 1;
  public price: number = this.product.price || 0;
  public recurring_value: number = this.product.recurring_value || 0;
  public open_price: boolean = this.product.open_price || false;
  public recurring_frequency_type: string = this.product.recurring_frequency_type || 'M';
  public recurring_frequency_type_select: string = this.product.recurring_frequency_type_select;
  public recurring_frequency: number = this.product.recurring_frequency ?? 1;
  public undetermined_charges: boolean = this.product.undetermined_charges || false;
  public limit_charges: number = this.product.limit_charges ?? 2;
  public overdue_limit: number = this.product.overdue_limit ?? 5;
  public permanent_access: boolean = this.product.permanent_access || false;
  public default_sales_page: string = this.product.default_sales_page || '';
  public deliver_first_charge: boolean = this.product.deliver_first_charge || false;
  public deliveries: Array<any> = this.product.deliveries || [];
  public description: string = this.product.description || '';
  public document_on_checkout: boolean = this.product.document_on_checkout || false;
  public email_welcome: boolean = this.product.email_welcome || false;
  public expiration_date: string = this.product.expiration_date || null;
  public friendly_name: string = this.product.friendly_name || '';
  public funnel_commission: number = this.product.funnel_commission || (this.product.infinity_funnel ? 50 : 0);
  public has_children: boolean = this.product.has_children || false;
  public has_expiration_date: boolean = this.product.has_expiration_date || false;
  public hide_quantity: boolean = this.product.hide_quantity || false;
  public id: number = this.product.id || 0;
  public image_id: number = this.product.image_id || null;
  public image: string = this.product.image || null;
  public infinity_funnel: boolean = this.product.infinity_funnel || false;
  public interest_free_installment: boolean = this.product.interest_free_installment || false;
  public is_producer: boolean = this.product.is_producer || false;
  public launch_date: string = this.product.launch_date || '';
  public marketplace_variation: number = this.product.marketplace_variation || null;
  public marketplace: boolean = this.product.marketplace || false;
  public membership_comission_type: string = this.product.membership_comission_type || 'P';
  public membership_comission_value: number = this.product.membership_comission_value || 0;
  public membership_fee: boolean = this.product.membership_fee || false;
  public membership_price: number = this.product.membership_price || 0;
  public moderation_status: string = this.product.moderation_status || '';
  public name_extract_description: string = this.product.name_extract_description || '';
  public notify_stock: number = this.product.notify_stock || 0;

  public parent_id: number = this.product.parent_product || null;
  public parent_product: number = this.product.parent_product || null;

  public physicalDelivery: any = this.product.physicalDelivery || null;
  public prelaunch: boolean = this.product.prelaunch || false;

  public private_label_rights: number = this.product.private_label_rights || 0;
  public producer_id: number = this.product.producer_id || 0;
  public program_content: string = this.product.program_content || '';
  public recovery_email: string = this.product.recovery_email || '';
  public recruit_commission_type: string = this.product.recruit_commission_type || 'P';
  public recruit_commission_value: number = this.product.recruit_commission_value || 0;
  public recruit_max_sales: number = this.product.recruit_max_sales || 0;
  public recruit: boolean = this.product.recruit || false;
  public recurring_comission_type: string = this.product.recurring_comission_type || 'P';
  public recurring_comission_value: number = this.product.recurring_comission_value || 0;
  public recurring_days_start_charging: number = this.product.recurring_days_start_charging || null;
  public recurring_start_charging: string = this.product.recurring_start_charging || '';
  public comission_on_first_invoice: boolean = this.product.comission_on_first_invoice || false;

  public refund_days: number = this.product.refund_days || 7;
  public remove_access: boolean = this.product.remove_access || false;
  public sales_expectation: number = this.product.sales_expectation || 0;
  public sales_recovery: boolean = this.product.sales_recovery || false;
  public stock: number = this.product.stock || 0;
  public sku: string = this.product.sku || '';
  public support_email: string = this.product.support_email || '';
  public thankyou_type: number = this.product.thankyou_type || 99;
  public title: string = this.product.title || '';
  public trial_days: number = this.product.trial_days || 1;
  public trial: boolean = this.product.trial || false;
  public type: number = this.product.type ?? 1;

  public unlimited_installment: boolean = this.product.unlimited_installment || false;
  public url_checkout: string = this.product.url_checkout || '';
  public url_thankyou_boleto: string = this.product.url_thankyou_boleto || '';
  public url_thankyou_card: string = this.product.url_thankyou_card || '';
  public visible: boolean = this.product.visible || false;
  public webhook_type: string = this.product.webhook_type || 'E';
  public webhook_url: string = this.product.webhook_url || '';
  public webhook: boolean = false;

  public yearly_installment = !!this.product.yearly_installment;

  public send_billing_reminder: boolean = this.product.send_billing_reminder || false;
  public plan_title = this.product.plan_title || '';
  public plan_description = this.product.plan_description || '';
  public prices = this.product.prices || [];
  public plan = this.product.plan || [];

  public hash?: string = this.product.hash;

  public allow_all_deliveries_for_free_charge_product?: boolean = true; //this.product.allow_all_deliveries_for_free_charge_product || false;

  private nullableFields: string[] = [
    'url_thankyou_card',
    'url_thankyou_boleto',
    'alternative_url',
    'affiliate_instructions',
    'affiliation_contract'
  ];

  constructor(private product: Partial<IProduct> = {}) {}

  public setCategories(categories: any): void {
    this.categories = categories;
  }

  public getIdCategories(): Array<number> {
    return this.categories && Array.isArray(this.categories)
      ? (this.categories.map((item: any) => (item && item.id ? item.id : item)) as Array<number>)
      : [];
  }

  public getRecurringStartCharging(): string {
    return this.recurring_start_charging ? formatDate(this.recurring_start_charging, 'yyyy-MM-dd') : null;
  }

  public getExpirationDate(): string {
    return this.expiration_date ? formatDate(this.expiration_date, 'yyyy-MM-dd') : '';
  }

  public getLaunchDate(): string {
    return this.launch_date ? formatDate(this.launch_date, 'yyyy-MM-dd') : '';
  }

  public hasVariations(): boolean {
    return this.has_children;
  }

  public isMainProduct(): boolean {
    return !this.parent_product;
  }

  public isVariation(): boolean {
    return this.parent_product && this.parent_id !== 0;
  }

  public isMainWithChildren(): boolean {
    return !this.parent_product && this.has_children;
  }

  public setParent(id: number): void {
    this.parent_product = id;
    this.parent_id = id;
  }

  public getCoProducers(): Array<ICoProducer> {
    return this.coproducers;
  }

  public setCoProducers(cps: Array<ICoProducer>): void {
    this.coproducers = cps || [];
  }

  public addNewCoProducer(cp: ICoProducer): void {
    this.coproducers.push(cp);
  }

  public removeCoProducer(cp: ICoProducer): void {
    this.coproducers = this.coproducers.filter((item: ICoProducer) => item !== cp);
  }

  public getNewCoProducers(): Array<ICoProducer> {
    return this.coproducers.filter((item: ICoProducer) => item.new);
  }

  public getCoProducersForSubmit(): Array<ICoProducer> {
    return this.coproducers.filter((item: ICoProducer) => (item.new || item.active) && !item.pending);
  }

  public getDeliveries(): Array<any> {
    return this.deliveries;
  }

  public setDeliveries(dvs: Array<any>): void {
    this.deliveries = dvs || [];
  }

  public addNewDelivery(dv: any): void {
    this.deliveries.push(dv);
  }

  public removeDelivery(dv: any): void {
    this.deliveries = this.deliveries.filter((item: any) => item !== dv);
  }

  public getPhysicalDelivery(): any {
    if (this.type === constants.PHYSICAL) {
      return this.product.physicalDelivery;
    }

    return null;
  }

  public setPhysicalDelivery(pd: any): void {
    if (this.type === constants.PHYSICAL) {
      this.physicalDelivery = pd;
    }
  }

  public getProductStatus(): number {
    return this.active;
  }

  public getGeneralProperties(): any {
    return {
      title: `${this.title} -  Variação ${formatDate(new Date(), 'dd/MM HH:mm')}`,
      description: this.description,
      image_id: this.image_id,
      image: this.image,
      expiration_date: this.expiration_date,
      has_expiration_date: this.has_expiration_date,
      type: this.type,
      author_name: this.author_name,
      alternative_url: this.alternative_url,
      refund_days: this.refund_days,
      prelaunch: this.prelaunch,
      launch_date: this.launch_date,
      sales_expectation: this.sales_expectation,
      program_content: this.program_content,
      support_email: this.support_email,
      categories: this.categories,
      friendly_name: this.friendly_name,
      auto_refund: this.auto_refund,
      marketplace: this.marketplace,
      marketplace_variation: this.marketplace_variation
    };
  }

  public getSalesProperties(): any {
    return {
      charge_type: this.charge_type,
      interest_free_installment: this.interest_free_installment,
      sales_recovery: this.sales_recovery,
      allows_boleto: this.allows_boleto,
      allows_pix: this.allows_pix,
      allows_automatic_pix: this.allows_automatic_pix,
      allows_combine_payment: this.allows_combine_payment,
      allows_paypal: this.allows_paypal,
      allows_multiple_cards: this.allows_multiple_cards,
      deliver_first_charge: this.deliver_first_charge,
      price: this.price,
      currency_id: this.currency_id,
      recurring_value: this.recurring_value,
      open_price: this.open_price,
      recurring_frequency_type: this.recurring_frequency_type,
      recurring_frequency: this.recurring_frequency,
      undetermined_charges: this.undetermined_charges,
      limit_charges: this.limit_charges,
      overdue_limit: this.overdue_limit,
      permanent_access: this.permanent_access,
      membership_fee: this.membership_fee,
      membership_price: this.membership_price,
      trial: this.trial,
      trial_days: this.trial_days,
      url_thankyou_boleto: this.url_thankyou_boleto,
      url_thankyou_card: this.url_thankyou_card,
      name_extract_description: this.name_extract_description,
      recovery_email: this.recovery_email,
      unlimited_installment: this.unlimited_installment,
      remove_access: this.remove_access,
      allows_card: this.allows_card
    };
  }
  public getAffiliatesProperties(): any {
    return {
      allow_affiliation: this.allow_affiliation,
      comission_type: this.comission_type,
      comission_value: this.comission_value,
      recurring_comission_type: this.recurring_comission_type,
      recurring_comission_value: this.recurring_comission_value,
      comission_on_first_invoice: this.comission_on_first_invoice,
      visible: this.visible,
      auto_affiliation: this.auto_affiliation,
      infinity_funnel: this.infinity_funnel,
      funnel_commission: this.funnel_commission,
      recruit: this.recruit,
      recruit_commission_type: this.recruit_commission_type,
      recruit_commission_value: this.recruit_commission_value,
      recruit_max_sales: this.recruit_max_sales,
      affiliate_instructions: this.affiliate_instructions,
      affiliation_contract: this.affiliation_contract,
      membership_comission_type: this.membership_comission_type,
      membership_comission_value: this.membership_comission_value
    };
  }
  public serviceFormat(): any {
    let image;

    if (this.image_id) {
      image = String(this.image_id);
    }

    const dateFormat = this.type === constants.TICKET ? 'MM/dd/yyyy HH:mm' : 'MM/dd/yyyy';

    const currencyId = 1;
    const recurringValue = functions.clearMoneyMask(this.recurring_value);
    const membershipPrice = functions.clearMoneyMask(this.membership_price);
    const price = functions.clearMoneyMask(this.price);
    const comissionValue = functions.clearMoneyMask(this.comission_value);
    const recruitCommissionValue = functions.clearMoneyMask(this.recruit_commission_value);
    const categories = this.getIdCategories();
    const recurringComissionValue = functions.clearMoneyMask(this.recurring_comission_value);
    const membershipComissionValue = functions.clearMoneyMask(this.membership_comission_value);
    const expirationDate = this.has_expiration_date ? formatDate(this.expiration_date, dateFormat) : null;
    const launchDate = this.prelaunch ? formatDate(this.launch_date, dateFormat) : null;

    const normalizedDeliveries = (this.deliveries || []).map((d: any) => {
      if (String(d.method_id) === '5' && Array.isArray(d.files)) {
        return {
          ...d,
          files: d.files.map((f: any) => (typeof f === 'object' && f.upload_id ? f.upload_id : f))
        };
      }

      return d;
    });

    const newFormatProduct: any = {
      ...(this as any),
      image,
      currency_id: currencyId,
      recurring_value: recurringValue,
      membership_price: membershipPrice,
      price,
      comission_value: comissionValue,
      recruit_commission_value: recruitCommissionValue,
      categories,
      recurring_comission_value: recurringComissionValue,
      membership_comission_value: membershipComissionValue,
      expiration_date: expirationDate,
      launch_date: launchDate,
      deliveries: normalizedDeliveries
    };

    newFormatProduct.allows_combine_payment = this.allows_combine_payment;

    const FRIENDLY_NAME_PREFIX = 'EDZ ';

    newFormatProduct.friendly_name =
      this.friendly_name && this.friendly_name.startsWith(FRIENDLY_NAME_PREFIX)
        ? this.friendly_name.slice(4)
        : this.friendly_name;

    const CUSTOM_RECURRING = 'P';

    if (this.recurring_frequency_type === CUSTOM_RECURRING) {
      if (this.recurring_frequency_type_select === 'MONTH') {
        newFormatProduct.recurring_frequency_type = 'M';
      }

      if (this.recurring_frequency_type_select === 'YEAR') {
        newFormatProduct.recurring_frequency_type = 'Y';
      }
    }

    //retirando espaço multiplos da descriçao
    //newFormatProduct.description = newFormatProduct.description.replace(/\s\s+/g, ' ');
    //se é produto físico
    if (this.type == 3) {
      newFormatProduct['type_freight'] = this.physicalDelivery.delivery_type;

      if (this.physicalDelivery.delivery_type == 3 || this.physicalDelivery.delivery_type == 4) {
        newFormatProduct['packing'] = this.physicalDelivery.format;
        //tipo de envios: SEDEX, SEDEX10 E PAC
        //40010,40215,41106

        newFormatProduct['type_delivery'] = this.physicalDelivery.delivery_options;

        newFormatProduct['cep_origin'] = this.physicalDelivery.zip_code;

        newFormatProduct['peso'] = functions.clearMoneyMask(this.physicalDelivery.weight);

        newFormatProduct['comprimento'] = this.physicalDelivery.length;

        if (this.physicalDelivery.format == 1 || this.physicalDelivery.delivery_type == 4) {
          newFormatProduct['largura'] = this.physicalDelivery.width;
          newFormatProduct['altura'] = this.physicalDelivery.height;
        }

        if (this.physicalDelivery.format == 2) {
          newFormatProduct['diametro'] = this.physicalDelivery.diameter;
        }
      }

      if (this.physicalDelivery.delivery_type == 2) {
        newFormatProduct['freight_value'] = functions.clearMoneyMask(this.physicalDelivery.delivery_value);
        newFormatProduct['deadline_delivery'] = this.physicalDelivery.delivery_days;
      }

      if (this.physicalDelivery.delivery_type == 1) {
        newFormatProduct['deadline_delivery'] = this.physicalDelivery.delivery_days;
      }
    }

    for (const key in newFormatProduct) {
      if (newFormatProduct[key] === '' || newFormatProduct[key] === null) {
        if (newFormatProduct[key] === '' && this.nullableFields.includes(key)) {
          continue;
        }

        delete newFormatProduct[key];
      }
    }

    if (newFormatProduct.id === 0) {
      if (!newFormatProduct.parent_id) {
        newFormatProduct.allows_multiple_cards = true;
        newFormatProduct.allows_boleto = true;
        newFormatProduct.allows_card = true;
      }

      delete newFormatProduct.id;
    }

    //TODO Verificar porque esta duplicando o object product
    delete newFormatProduct.product;

    // Se for vitrine nao mandar essas info
    if (this.isMainWithChildren()) {
      delete newFormatProduct.url_thankyou_card;
      delete newFormatProduct.url_thankyou_boleto;
      delete newFormatProduct.recovery_email;
      delete newFormatProduct.price;
      delete newFormatProduct.name_extract_description;
      delete newFormatProduct.recurring_value;
      delete newFormatProduct.recurring_comission_type;
      delete newFormatProduct.recurring_comission_value;
      delete newFormatProduct.recurring_frequency;
      delete newFormatProduct.recurring_frequency_type;
      delete newFormatProduct.limit_charges;
      delete newFormatProduct.overdue_limit;
      delete newFormatProduct.permanent_access;
      delete newFormatProduct.membership_price;
      delete newFormatProduct.trial_days;
      delete newFormatProduct.charge_type;
      newFormatProduct.coproducers = newFormatProduct.coproducers.filter((cp: any) => !cp.pending);
    }

    if (this.charge_type === EChargeType.FREE) {
      newFormatProduct.allows_multiple_cards = false;
      newFormatProduct.allows_combine_payment = false;
      delete newFormatProduct.comission_type;
      delete newFormatProduct.comission_value;
      delete newFormatProduct.recruit_commission_type;
      delete newFormatProduct.recruit_commission_value;
      delete newFormatProduct.recurring_comission_type;
      delete newFormatProduct.recurring_comission_value;
    }

    if (this.charge_type === EChargeType.SUBSCRIPTION) {
      newFormatProduct.allows_multiple_cards = false;
    }

    if (this.thankyou_type === 99) newFormatProduct.thankyou_type = 0;

    return newFormatProduct;
  }
}

export default Product;
